/**
 * Gathers the registries an environment label can come from and hands back
 * one resolver. The sidebar assembles the same registries for its rows; this
 * hook reuses its desktop runtime map rather than re-deriving it.
 *
 * @module usage/useUsageEnvironmentLabels
 */
import { useCallback, useMemo } from "react";

import type { EnvironmentTotals } from "@cmmd-forge/shared/usageMerge";

import { useCmmdDeviceRegistrySnapshot } from "../cmmd/cmmdDeviceRegistryResource";
import { useCmmdRuntimeInventory } from "../cmmd/useCmmdRuntimeInventory";
import { buildRegisteredDesktopRuntimeMap } from "../components/sidebar/registeredDesktopRuntimeMap";
import { getStoredLocalDeviceKey } from "../deviceRegistryIdentity";
import { useSavedEnvironmentRegistryStore } from "../environments/runtime";
import {
  buildUsageEnvironmentLabelContext,
  deviceRegistryLists,
  resolveUsageEnvironmentLabel,
} from "./usageEnvironmentLabels";
import type { UsageSourceStatus } from "./useUsage";

export function useUsageEnvironmentLabels(
  sources: readonly UsageSourceStatus[],
): (row: Pick<EnvironmentTotals, "runtimeId" | "sourceId">) => string {
  const registry = deviceRegistryLists(useCmmdDeviceRegistrySnapshot().registry);
  const inventory = useCmmdRuntimeInventory({
    registeredRuntimes: registry.runtimes,
    registeredDevices: registry.devices,
  });
  const savedEnvironmentsById = useSavedEnvironmentRegistryStore((state) => state.byId);

  const context = useMemo(
    () =>
      buildUsageEnvironmentLabelContext({
        sources,
        desktopRuntimes: [
          ...buildRegisteredDesktopRuntimeMap({
            registeredRuntimes: registry.runtimes,
            registeredDevices: registry.devices,
            registeredHosts: registry.hosts,
            currentRegisteredRuntimeId: inventory.currentRegisteredRuntimeId,
            currentRegisteredDeviceKey: getStoredLocalDeviceKey(),
          }).entries(),
        ],
        inventoryRuntimes: inventory.runtimes,
        savedEnvironments: Object.values(savedEnvironmentsById),
      }),
    [
      sources,
      registry,
      inventory.currentRegisteredRuntimeId,
      inventory.runtimes,
      savedEnvironmentsById,
    ],
  );

  return useCallback((row) => resolveUsageEnvironmentLabel(row, context), [context]);
}
